import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsCalendar, BsGoogle, BsMicrosoft, BsTrash } from 'react-icons/bs';

import Card from '#globalComponents/Card/Card';
import Switch from '#globalComponents/Forms/Switch';
import IconButton from '#globalComponents/IconButton/IconButton';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useErrorStore } from '#lib/stores/errorStore';

import {
	useStartCalendarOAuth,
	useDisconnectCalendar,
	useUpdateCalendarConnection,
} from '../../../../models/calendar/mutations';
import { useCalendarConnections } from '../../../../models/calendar/queries';

const PROVIDER_META = {
	google_calendar: { label: 'Google Calendar', icon: BsGoogle, color: 'text-warning-500' },
	microsoft_calendar: { label: 'Microsoft 365', icon: BsMicrosoft, color: 'text-blue-500' },
};

const CalendarConfigModal = ({ isOpen, onClose }) => {
	const showError = useErrorStore((s) => s.showError);
	const { data: connections = [], isLoading } = useCalendarConnections();
	const startOAuth = useStartCalendarOAuth();
	const disconnectMutation = useDisconnectCalendar();
	const updateMutation = useUpdateCalendarConnection();

	const [disconnectTarget, setDisconnectTarget] = useState(null);

	const handleConnect = (provider) => {
		startOAuth.mutate(provider, {
			onError: (err) => showError({ error: err, title: 'Kalender-Verbindung konnte nicht gestartet werden' }),
		});
	};

	const handleDisconnect = () => {
		if (!disconnectTarget) return;
		disconnectMutation.mutate(disconnectTarget, {
			onSuccess: () => setDisconnectTarget(null),
			onError: (err) => showError({ error: err, title: 'Verbindung konnte nicht getrennt werden' }),
		});
	};

	const handleToggleSync = (conn) => {
		updateMutation.mutate({
			id: conn.id,
			settings: { syncEnabled: !conn.metadata?.syncEnabled },
		});
	};

	return (
		<>
			<Modal isOpen={isOpen} onClose={onClose} size="m">
				<Panel.Header icon={BsCalendar} onClose={onClose} subtitle="Google Calendar & Microsoft 365">
					Kalender
				</Panel.Header>
				<Panel.Body className="space-y-6">
					{connections.length > 0 && (
						<div className="space-y-3">
							<h3 className="text-sm font-medium text-text">Verbundene Kalender</h3>
							{connections.map((conn) => {
								const meta = PROVIDER_META[conn.provider] ?? {};
								const ProviderIcon = meta.icon ?? BsCalendar;
								return (
									<Card key={conn.id}>
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-3">
												<ProviderIcon
													className={`w-4 h-4 ${meta.color ?? 'text-bgBase-800'}`}
												/>
												<div>
													<p className="text-sm font-medium text-text">{conn.label}</p>
													<p className="text-xs text-bgBase-800">
														{meta.label} · {conn.metadata?.calendarEmail}
													</p>
												</div>
											</div>
											<div className="flex items-center gap-2">
												<Switch
													size="sm"
													checked={Boolean(conn.metadata?.syncEnabled)}
													onChange={() => handleToggleSync(conn)}
													disabled={updateMutation.isPending}
													title={conn.metadata?.syncEnabled ? 'Sync aktiv' : 'Sync pausiert'}
												/>
												<IconButton
													icon={BsTrash}
													label="Verbindung trennen"
													tone="danger"
													onClick={() => setDisconnectTarget(conn.id)}
												/>
											</div>
										</div>
									</Card>
								);
							})}
						</div>
					)}

					<div className="space-y-3">
						<h3 className="text-sm font-medium text-text">
							{connections.length > 0 ? 'Weiteren Kalender verbinden' : 'Kalender verbinden'}
						</h3>
						<div className="grid grid-cols-2 gap-3">
							<button
								onClick={() => handleConnect('google-calendar')}
								disabled={startOAuth.isPending}
								className="flex items-center gap-3 p-4 rounded-card border border-bgBase-500 hover:border-primary-300 hover:bg-primary-100 transition-colors"
							>
								<BsGoogle className="w-5 h-5 text-warning-500" />
								<div className="text-left">
									<p className="text-sm font-medium text-text">Google</p>
									<p className="text-xs text-bgBase-800">Calendar + Meet</p>
								</div>
							</button>
							<button
								onClick={() => handleConnect('microsoft-calendar')}
								disabled={startOAuth.isPending}
								className="flex items-center gap-3 p-4 rounded-card border border-bgBase-500 hover:border-primary-300 hover:bg-primary-100 transition-colors"
							>
								<BsMicrosoft className="w-5 h-5 text-blue-500" />
								<div className="text-left">
									<p className="text-sm font-medium text-text">Microsoft</p>
									<p className="text-xs text-bgBase-800">Outlook + Teams</p>
								</div>
							</button>
						</div>
					</div>

					{isLoading && (
						<div className="flex items-center gap-2 py-3 text-xs text-bgBase-800">
							<div className="animate-spin h-4 w-4 border-2 border-bgBase-600 border-t-transparent rounded-full" />
							Kalender werden geladen…
						</div>
					)}

					<div className="bg-primary-100 border border-primary-200 rounded-card p-4">
						<p className="text-xs text-primary-700">
							Verbinden Sie Ihren Kalender, um Meetings automatisch zu synchronisieren. Jeder Benutzer
							kann mehrere Kalender verbinden (z.B. Geschäft + Privat). Nur Meetings werden synchronisiert
							— Aufgaben und Notizen bleiben CRM-intern.
						</p>
					</div>
				</Panel.Body>
			</Modal>

			<ConfirmModal
				isOpen={Boolean(disconnectTarget)}
				onClose={() => setDisconnectTarget(null)}
				onConfirm={handleDisconnect}
				title="Kalender trennen"
				message="Möchten Sie diese Kalender-Verbindung wirklich trennen? Die Synchronisation wird gestoppt. Bestehende Aktivitäten bleiben erhalten."
				confirmText="Trennen"
				isLoading={disconnectMutation.isPending}
			/>
		</>
	);
};

CalendarConfigModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default CalendarConfigModal;
